import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Leaf, LogOut, LayoutDashboard, FileText, Package, Image as ImageIcon, Inbox, ShoppingCart, ArrowLeft, Tractor, Sparkles, FolderOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, eur } from "@/lib/api";
import AdminLogin from "@/pages/admin/AdminLogin";
import PostsManager from "@/pages/admin/PostsManager";
import ProductsManager from "@/pages/admin/ProductsManager";
import WorksManager from "@/pages/admin/WorksManager";
import QuotesManager from "@/pages/admin/QuotesManager";
import OrdersManager from "@/pages/admin/OrdersManager";
import AgriProductsManager from "@/pages/admin/AgriProductsManager";
import AiAssistant from "@/pages/admin/AiAssistant";
import MediaLibrary from "@/pages/admin/MediaLibrary";

const TABS = [
  { id: "panoramica", label: "Panoramica", icon: LayoutDashboard },
  { id: "ai", label: "Assistente AI", icon: Sparkles },
  { id: "articoli", label: "Articoli", icon: FileText },
  { id: "prodotti", label: "Prodotti", icon: Package },
  { id: "agrieuro", label: "AgriEuro", icon: Tractor },
  { id: "lavori", label: "Lavori", icon: ImageIcon },
  { id: "media", label: "Media", icon: FolderOpen },
  { id: "preventivi", label: "Preventivi", icon: Inbox },
  { id: "ordini", label: "Ordini", icon: ShoppingCart },
];

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <p className="text-sage" data-testid="stats-loading">Caricamento…</p>;
  const cards = [
    { label: "Articoli pubblicati", value: stats.posts, testid: "stat-posts" },
    { label: "Prodotti a catalogo", value: stats.products, testid: "stat-products" },
    { label: "Lavori nel portfolio", value: stats.works, testid: "stat-works" },
    { label: "Preventivi (nuovi)", value: `${stats.quotes} (${stats.new_quotes})`, testid: "stat-quotes" },
    { label: "Ordini completati", value: stats.orders, testid: "stat-orders" },
    { label: "Fatturato", value: eur(stats.revenue), testid: "stat-revenue" },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-overview">
      {cards.map((c) => (
        <div key={c.label} className="rounded-sm border border-line bg-bone p-6" data-testid={c.testid}>
          <p className="font-accent text-[10px] uppercase tracking-[0.22em] text-sage">{c.label}</p>
          <p className="mt-3 font-serif text-4xl text-forest">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("panoramica");

  useEffect(() => { document.title = "Pannello Admin — Luca Mannu Giardini"; }, []);

  if (user === null)
    return <main className="flex min-h-screen items-center justify-center bg-bone text-sage" data-testid="admin-loading">Caricamento…</main>;
  if (user === false) return <AdminLogin />;

  return (
    <main className="min-h-screen bg-sand/40" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-line bg-bone/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <Leaf className="h-5 w-5 text-terra" strokeWidth={1.5} />
            <span className="font-serif text-xl tracking-tight text-forest">Luca Mannu <span className="italic">Giardini</span></span>
            <span className="ml-3 rounded-full bg-forest px-3 py-1 font-accent text-[10px] uppercase tracking-[0.2em] text-bone">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="link-underline hidden items-center gap-2 font-accent text-xs uppercase tracking-[0.2em] text-sage md:flex" data-testid="admin-back-site">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Sito
            </Link>
            <button onClick={logout} className="flex items-center gap-2 rounded-full border border-line px-4 py-2 font-accent text-xs uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-forest hover:text-bone" data-testid="admin-logout-button">
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Esci
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-6 pb-3" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 font-accent text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                tab === t.id ? "bg-forest text-bone" : "text-sage hover:text-forest"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-10">
        {tab === "panoramica" && <Overview />}
        {tab === "ai" && <AiAssistant />}
        {tab === "articoli" && <PostsManager />}
        {tab === "prodotti" && <ProductsManager />}
        {tab === "agrieuro" && <AgriProductsManager />}
        {tab === "lavori" && <WorksManager />}
        {tab === "media" && <MediaLibrary />}
        {tab === "preventivi" && <QuotesManager />}
        {tab === "ordini" && <OrdersManager />}
      </div>
    </main>
  );
}
